<template src="@/components/templates/profile.html"></template>

<script>
import { mapGetters, mapActions } from 'vuex'
import FileUpload from 'vue-upload-component'
import Cropper from 'cropperjs'
import Datepicker from 'vuejs-datepicker'
import PlayerUser from '@/models/PlayerUser'
import Switcher from '@/components/common/form/Switcher'
import CustomInput from '@/components/common/form/CustomInput'
import STATUS_CODES from '@/common/statusCodes';
import { TOASTER_SUCCESS, TOASTER_ERROR } from '@/common/events.types';
import moment from 'moment';
import _ from 'lodash';
import ApiService from '@/common/api.service';
import { CHANGE_PASSWORD_PROFILE_URL } from '@/constants/ApiConstants';
import {
  EDIT_PROFILE,
  UNSUBSCRIBE_EMAIL,
  GET_PROFILE_DATA,
  EDIT_EMAIL,
  UPDATE_PRIVACY,
  GET_PLAYER_POSITIONS,
  UPDATE_UNITS
} from '@/store/action.types';
import Privacy from '@/components/profile/Privacy'
import { FocusField } from '@/directives/FocusFieldDirective'
import CheckFormValidity from '@/directives/CheckFormValidity'
import AdditionalEmail from '@/components/profile/AdditionalEmail'
import LanguagePicker from '@/components/common/LanguagePicker'
import { en, fr } from 'vuejs-datepicker/dist/locale'
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';
import { UNIT_SYSTEM } from '../../constants/UnitConsts'
import {
  IN_PER_FT,
  toNum,
  resolveHeightCm,
  resolveWeightKg,
  resolveMetricHeightCm,
  resolveMetricWeightKg,
  heightDisplay,
  weightDisplay
} from '@/common/units'
import { toIsoDateString, withDateParts } from '@/common/dates'

export default {
  name: 'Profile',
  data() {
    return {
      files: [],
      edit: false,
      cropper: false,
      serverError: false,
      formSubmitted: false,
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
      profileInfoFormSubmitted: false,
      userToEdit: {},
      unsubscribe: false,
      unsubscribeWeekly: null,
      searchText: '',
      validationRules: new PlayerUser().validationRules(),
      emailChanged: false,
      loadingForm: false,
      loadingAccountForm: false,
      changePassword: 'changePassword',
      editProfile: 'editProfile',
      en: en,
      fr: fr,
      calledFromIos: false,
      feetInput: '',
      inchInput: '',
      phoneValid: null,
      phoneNumber: '',
      countryCode: null,
      positions: [],
      selectedPosition: null,
      // Stored (metric) values; the inputs above may be showing them in imperial
      metricHeight: null,
      metricWeight: null,
    }
  },
  directives: {
    FocusField,
    CheckFormValidity
  },
  computed: {
    ...mapGetters([
      'user',
      'account',
      'userEmail',
      'subAccounts',
      'playerPositions'
    ]),

    dayOfBirth: {
      get() {
        return moment(this.userToEdit.dob, 'D')
      },
      set(newDay) {
        this.userToEdit.dob = newDay
      }
    },
    monthOfBirth: {
      get() {
        return moment(this.userToEdit.dob, 'M')
      },
      set(newDay) {
        this.userToEdit.monthOfBirth = newDay
      }
    },
    yearOfBirth: {
      get() {
        return moment(this.userToEdit.dob, 'Y')
      },
      set(newDay) {
        this.userToEdit.yearOfBirth = newDay
      }
    },
    isDistanceImperial() {
      return this.account.unitDistance === UNIT_SYSTEM.IMPERIAL;
    },
    isHeightWeightImperial() {
      return this.account.unitHeightWeight === UNIT_SYSTEM.IMPERIAL;
    }
  },
  mounted() {
    let context = this;
    // call from iOS
    window.sendMethodToIos = function () {
      context.calledFromIos = true;
      document.querySelector('#fileUploadWrapper label').click();
    }

    this.userToEdit = Object.assign({}, this.account)

    if (typeof (this.account.phone) === "string") {
      this.userToEdit.phone = {
        countryCode: this.account.phone.substring(0, 2),
        number: ''
      }
    }
    this.getProfileData(this.user.accountId)
      .then(data => {
        this.unsubscribe = this.account.subscriptions.sessionReport.subscribed;
        this.unsubscribeWeekly = this.account.subscriptions.weeklyReport.subscribed;
        this.phoneNumber = this.account.phone.number;
        this.countryCode = this.account.phone.countryCode;
      })

    const input = document.querySelector("#phone");
    this.iti = intlTelInput(input, {
      utilsScript: "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js",
      initialCountry: context.userToEdit.phone === null ? "us" : context.userToEdit.phone.countryCode,
      preferredCountries: ["us", "ca", "gb", "de"],
      nationalMode: true,
      autoPlaceholder: "off",
      formatOnDisplay: false
    });

    function handleCountryChange() {
      let changedCountryData = context.iti.getSelectedCountryData();
      context.countryCode = changedCountryData.iso2;
      context.iti.isValidNumber() ? context.phoneValid = true : context.phoneValid = false;
      if (context.phoneValid) {
        context.$validator.errors.removeById('invalidPhone');
      } else if (!context.phoneValid && !context.phoneNumber) {
        context.$validator.errors.removeById('invalidPhone');
      }
    }

    function handlePhoneChange() {
      context.iti.isValidNumber() ? context.phoneValid = true : context.phoneValid = false;
      if (context.phoneValid) {
        context.$validator.errors.removeById('invalidPhone');
      } else if (!context.phoneValid && !context.phoneNumber) {
        context.$validator.errors.removeById('invalidPhone');
      }
    };

    input.addEventListener("countrychange", handleCountryChange);
    input.addEventListener('change', handlePhoneChange);

    this.getPlayerPositions();

    this.initHeightWeightUIFromAccount();
  },

  beforeUpdate() {
    if (this.userToEdit.position) {
      this.definePlayerPosition(this.userToEdit.position);
    }
  },

  beforeDestroy() {
    this.iti.destroy();
  },
  watch: {
    edit(value) {
      if (value) {
        this.$nextTick(function () {
          if (!this.$refs.editImage) {
            return
          }
          let cropper = new Cropper(this.$refs.editImage, {
            viewMode: 1,
            aspectRatio: 1
          })
          this.cropper = cropper
        })
      } else {
        if (this.cropper) {
          this.cropper.destroy()
          this.cropper = false
        }
      }
    },
    account: function (newValue) {
      this.files = []
      this.userToEdit = Object.assign({}, newValue)
      this.$validator.errors.removeById('wrongPassword')
      this.$validator.errors.removeById('emailTaken')
      this.$validator.errors.removeById('invalidPhone')
      this.profileInfoFormSubmitted = false
      this.phoneNumber = this.userToEdit.phone.number
      this.iti.setCountry(newValue.phone.countryCode || "us");
      this.initHeightWeightUIFromAccount(newValue);
    }
  },
  methods: {
    ...mapActions({
      editProfileAction: EDIT_PROFILE,
      unsubscribeAction: UNSUBSCRIBE_EMAIL,
      getProfileData: GET_PROFILE_DATA,
      editEmailAction: EDIT_EMAIL,
      updatePrivacyAction: UPDATE_PRIVACY,
      getPlayerPositionsAction: GET_PLAYER_POSITIONS,
      updateUnitsAction: UPDATE_UNITS
    }),

    getPlayerPositions() {
      this.getPlayerPositionsAction();
    },

    validateAndSubmitProfileInfoForm() {
      this.$amplitude.logEvent('pwa: save profile info')
      if (this.emailChanged) {
        this.$amplitude.logEvent('pwa: change profile email')
      }
      if (this.phoneNumber != this.account.phone.number || this.countryCode !== this.account.phone.countryCode) {
        this.$amplitude.logEvent('pwa: change profile number')
      }

      let context = this;
      context.iti.isValidNumber() ? context.phoneValid = true : context.phoneValid = false;
      this.$validator.validateAll('editProfile').then(result => {
        this.profileInfoFormSubmitted = true
        if (!this.phoneValid && context.phoneNumber) {
          context.$validator.errors.add({
            field: 'phone',
            msg: 'Invalid Phone number.',
            scope: 'editProfile',
            id: 'invalidPhone'
          })
          return false
        }
        if (this.emailChanged) {
          if (result) {
            this.editEmailAction({
              accountId: this.user.accountId,
              email: this.userToEdit.email,
              password: this.userToEdit.password
            })
              .then(() => {
                this.editProfileFields()

                this.userToEdit.email = this.user.email
                this.emailChanged = false
                this.userToEdit.password = ''
              })
              .catch(({ status }) => {
                if (status.code === STATUS_CODES.BP_400) {
                  this.$validator.errors.add({
                    field: 'password',
                    msg: 'Wrong Password.',
                    scope: 'editProfile',
                    id: 'wrongPassword'
                  })
                } else if (status.code === STATUS_CODES.BP_409) {
                  this.$validator.errors.add({
                    field: 'email',
                    msg: 'Email is already taken.',
                    scope: 'editProfile',
                    id: 'emailTaken'
                  })
                } else if (status.code === STATUS_CODES.BP_403) {
                  this.$eventHub.$emit(TOASTER_ERROR, { message: this.$t('PROFILE.toastMessages.accountActivated') })
                }
              })
          }
        } else {
          if (result) {
            this.editProfileFields()
          }
        }
      })
    },
    editProfileFields() {
      this.userToEdit.dob = toIsoDateString(this.userToEdit.dob)
      this.userToEdit.playerImage = this.userToEdit.photo
      delete this.userToEdit.photo
      let userFormData = new FormData()

      for (let property in this.userToEdit) {
        if (this.userToEdit[property] !== null && this.userToEdit[property] !== undefined && property !== "phone") {
          userFormData.append(property, this.userToEdit[property])
        }
      }

      if (this.phoneNumber) {
        userFormData.append('phone.countryCode', this.countryCode.toUpperCase());
        userFormData.append('phone.number', this.phoneNumber);
      }

      this.syncMetricFromInputs();
      userFormData.delete('height');
      userFormData.delete('weight');
      if (this.metricHeight != null) userFormData.set('height', this.metricHeight);
      if (this.metricWeight != null) userFormData.set('weight', this.metricWeight);

      this.loadingForm = true

      this.editProfileAction({ data: userFormData, id: this.userToEdit.playerId })
        .then(() => {
          this.$eventHub.$emit(TOASTER_SUCCESS, { message: this.$t('PROFILE.toastMessages.profileUpdated') })
          this.serverError = false
          this.profileInfoFormSubmitted = false
          this.loadingForm = false
        })
        .catch(() => {
          this.$eventHub.$emit(TOASTER_ERROR, { message: this.$t('TOAST_MESSAGES.otherError') })
          this.loadingForm = false
        })
    },
    validateAndSubmitChangePasswordForm() {
      this.$validator.validateAll('changePassword').then(result => {
        this.formSubmitted = true
        if (result) {
          this.loadingAccountForm = true
          ApiService.putData(CHANGE_PASSWORD_PROFILE_URL.replace(':accountId', this.user.accountId), {
            currentPassword: this.currentPassword,
            newPassword: this.newPassword,
            confirmPassword: this.confirmNewPassword
          })
            .then(() => {
              this.$eventHub.$emit(TOASTER_SUCCESS, { message: this.$t('PROFILE.toastMessages.passwordChanged') })
              this.serverError = false
              this.formSubmitted = false
              this.currentPassword = ''
              this.newPassword = ''
              this.confirmNewPassword = ''
              this.loadingAccountForm = false
            })
            .catch(({ status }) => {
              if (status.code === STATUS_CODES.BP_400) {
                this.$eventHub.$emit(TOASTER_ERROR, { message: this.$t('PROFILE.toastMessages.invalidPassword') })
              } else {
                this.$eventHub.$emit(TOASTER_ERROR, { message: this.$t('TOAST_MESSAGES.otherError') })
              }
              this.loadingAccountForm = false
            })
        }
      })
    },

    editSave() {
      this.edit = false
      let oldFile = this.files[0]
      let binStr = atob(
        this.cropper
          .getCroppedCanvas()
          .toDataURL(oldFile.type)
          .split(',')[1]
      )
      let arr = new Uint8Array(binStr.length)
      for (let i = 0; i < binStr.length; i++) {
        arr[i] = binStr.charCodeAt(i)
      }
      let file = new File([arr], oldFile.name, { type: oldFile.type })
      this.$refs.upload.update(oldFile.id, {
        file,
        type: file.type,
        size: file.size,
        active: true
      })
      this.userToEdit.photo = file

    },
    inputFile(newFile, oldFile) {
      if (newFile && !oldFile) {
        this.$nextTick(() => {
          this.edit = true
        })
      }
      if (!newFile && oldFile) {
        this.edit = false
      }
    },
    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        if (!/\.(gif|jpg|jpeg|png|webp)$/i.test(newFile.name)) {
          return prevent()
        }
      }

      if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
        newFile.url = ''
        let URL = window.URL || window.webkitURL
        if (URL && URL.createObjectURL) {
          newFile.url = URL.createObjectURL(newFile.file)
        }
      }
    },
    onDaySelect(date) {
      this.userToEdit.dob = toIsoDateString(withDateParts(this.userToEdit.dob, { day: date.getDate() }))
    },
    onMonthSelect(date) {
      this.userToEdit.dob = toIsoDateString(withDateParts(this.userToEdit.dob, { month: date.getMonth() }))
    },
    onYearSelect(date) {
      this.userToEdit.dob = toIsoDateString(withDateParts(this.userToEdit.dob, { year: date.getFullYear() }))
    },
    unsubscribeFromEmailAndLog() {
      this.unsubscribe = !this.account.subscriptions.sessionReport.subscribed
      this.$amplitude.logEvent('pwa: change session summary subscription', { isSubscribed: this.unsubscribe })

      this.unsubscribeAction({
        subscriptionId: this.account.subscriptions.sessionReport.subscriptionId,
        subscribed: this.unsubscribe
      })
        .then(() => {
          this.$eventHub.$emit(TOASTER_SUCCESS, { message: this.$t('PROFILE.toastMessages.notificationUpdated') })
          this.getProfileData(this.user.accountId)
        })
        .catch(({ status }) => {
          this.$eventHub.$emit(TOASTER_ERROR, { message: this.$t('TOAST_MESSAGES.otherError') })
        })
    },
    unsubscribeFromWeeklyEmailAndLog() {
      this.unsubscribeWeekly = !this.account.subscriptions.weeklyReport.subscribed
      this.$amplitude.logEvent('pwa: change weekly report subscription', { isSubscribed: this.unsubscribeWeekly })

      this.unsubscribeAction({
        subscriptionId: this.account.subscriptions.weeklyReport.subscriptionId,
        subscribed: this.unsubscribeWeekly
      })
        .then(() => {
          this.$eventHub.$emit(TOASTER_SUCCESS, { message: this.$t('PROFILE.toastMessages.notificationUpdated') })
          this.getProfileData(this.user.accountId)
        })
        .catch(() => {
          this.$eventHub.$emit(TOASTER_ERROR, { message: this.$t('TOAST_MESSAGES.otherError') })
        })
    },

    onEmailChange() {
      this.$validator.errors.removeById('emailTaken')

      if (this.userToEdit.email === this.account.email) {
        this.emailChanged = false
      } else {
        this.emailChanged = true
      }
    },

    onPasswordChange() {
      this.$validator.errors.removeById('wrongPassword')
    },


    sendUrlToIos(event) {
      if (navigator.platform.substr(0, 2) === 'iP') {
        //iOS (iPhone, iPod or iPad)
        let lte9 = /constructor/i.test(window.HTMLElement);
        let nav = window.navigator, ua = nav.userAgent, idb = !!window.indexedDB;

        if ((ua.indexOf('Safari') !== -1 && ua.indexOf('Version') !== -1 && !nav.standalone) || ((window.webkit && window.webkit.messageHandlers) || !lte9 || idb)) {
          //Safari (WKWebView/Nitro since 6+)
          let appName = 'bp';
          let actionType = 'upload-image';
          let methodForInput = 'inputFile'
          let url = appName + '://' + actionType;
          // check for permissions if yes trigger click if no dont open dialog (check is performed on iOS side)
          if (!this.calledFromIos) {
            window.location = url;
            event.preventDefault();
          }
        }
      }
    },
    selectPlayerPosition(position, description) {
      const positionObj = {
        position,
        description
      }
      this.selectedPosition = positionObj;
    },
    definePlayerPosition(positionDescription) {
      if (positionDescription) {
        switch (positionDescription) {
          case "Striker":
            this.userToEdit.position = {
              position: "ST",
              description: positionDescription
            }
            break;
          case "Center Forward":
            this.userToEdit.position = {
              position: "CF",
              description: positionDescription
            }
            break;
          case "Winger":
            this.userToEdit.position = {
              position: "W",
              description: positionDescription
            }
            break;
          case "Center Attacking Midfielder":
            this.userToEdit.position = {
              position: "CAM",
              description: positionDescription
            }
            break;
          case "Center Midfielder":
            this.userToEdit.position = {
              position: "CM",
              description: positionDescription
            }
            break;
          case "Center Defensive Midfielder":
            this.userToEdit.position = {
              position: "CDM",
              description: positionDescription
            }
            break;
          case "Wide Midfielder":
            this.userToEdit.position = {
              position: "WM",
              description: positionDescription
            }
            break;
          case "Wing-back":
            this.userToEdit.position = {
              position: "WB",
              description: positionDescription
            }
            break;
          case "Center Back":
            this.userToEdit.position = {
              position: "CB",
              description: positionDescription
            }
            break;
          case "Goal Keeper":
            this.userToEdit.position = {
              position: "GK",
              description: positionDescription
            }
            break;
        }
      } else {
        this.userToEdit.position = null;
      }
    },
    updateUnitsDistance(units) {
      const { unitDistance, unitHeightWeight, playerId } = this.account
      const newUnitDistance = unitDistance === UNIT_SYSTEM.IMPERIAL ? UNIT_SYSTEM.METRIC : UNIT_SYSTEM.IMPERIAL;
      const newUnitHeightWeight = unitHeightWeight || UNIT_SYSTEM.METRIC; // in case if unitHeightWeight value is null
      this.updateUnitsAction({ unitDistance: newUnitDistance, unitHeightWeight: newUnitHeightWeight, playerId }).then((data) => {
        this.getProfileData(this.user.accountId)
        this.$eventHub.$emit(TOASTER_SUCCESS, { message: this.$t('PROFILE.toastMessages.profileUpdated') })
      })
        .catch((error) => {
        })
    },
    async updateUnitsHeightWeight() {
      const { unitDistance, unitHeightWeight, playerId } = this.account;
      const wasImperial = unitHeightWeight === UNIT_SYSTEM.IMPERIAL;
      const newUnit = wasImperial ? UNIT_SYSTEM.METRIC : UNIT_SYSTEM.IMPERIAL;

      this.syncMetricFromInputs();
      this.account.unitHeightWeight = newUnit;
      this.renderHeightWeightInputs();

      try {
        await this.updateUnitsAction({
          unitDistance: unitDistance || UNIT_SYSTEM.METRIC,
          unitHeightWeight: newUnit,
          playerId
        });
        this.$eventHub.$emit(TOASTER_SUCCESS, { message: this.$t('PROFILE.toastMessages.profileUpdated') });
      } catch (e) {
        this.$eventHub.$emit(TOASTER_ERROR, { message: this.$t('TOAST_MESSAGES.otherError') });
        this.account.unitHeightWeight = unitHeightWeight;
        this.renderHeightWeightInputs();
      }
    },
    normalizeInchesOverflow() {
      const ft = toNum(this.feetInput) || 0;
      const inch = toNum(this.inchInput);
      if (inch != null && inch >= IN_PER_FT) {
        this.feetInput = ft + Math.floor(inch / IN_PER_FT);
        this.inchInput = inch % IN_PER_FT;
      }
    },

    initHeightWeightUIFromAccount(acc = this.account) {
      this.metricHeight = toNum(acc.height) || null;
      this.metricWeight = toNum(acc.weight) || null;
      this.renderHeightWeightInputs();
    },

    renderHeightWeightInputs() {
      const imperial = this.isHeightWeightImperial;
      const height = heightDisplay(this.metricHeight, imperial);
      if (imperial) {
        this.feetInput = height.ft;
        this.inchInput = height.inch;
      } else {
        this.userToEdit.height = height;
      }
      this.userToEdit.weight = weightDisplay(this.metricWeight, imperial);
    },

    // Updates the stored metric values from whatever the inputs currently show
    syncMetricFromInputs() {
      if (this.isHeightWeightImperial) {
        this.metricHeight = resolveHeightCm(this.metricHeight, this.feetInput, this.inchInput);
        this.metricWeight = resolveWeightKg(this.metricWeight, this.userToEdit.weight);
      } else {
        this.metricHeight = resolveMetricHeightCm(this.metricHeight, this.userToEdit.height);
        this.metricWeight = resolveMetricWeightKg(this.metricWeight, this.userToEdit.weight);
      }
    },
  },
  components: {
    FileUpload,
    Cropper,
    Datepicker,
    Switcher,
    CustomInput,
    Privacy,
    AdditionalEmail,
    LanguagePicker
  }
}
</script>

<style lang="scss" scoped>
@import '@/scss/views/_profile.scss';

.phone-number-valid {
  color: $mountain-meadow;

  &.invalid {
    color: $alizarin-crimson;
  }
}

.imperial-fields {
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 600px;

  .imperial-fields-input {
    width: 45%;
  }

  .imperial-fields-units {
    padding-top: 15px;
    margin-bottom: 0;
    color: #5E5E62;
  }
}

.profile-input-max-width {
  max-width: 600px;
}

@media screen and (max-width: 768px) {
  .profile-input {
    width: 100%;
  }
}

.section {
  margin-top: 0;
}

.section-content {
  min-height: 0;
}

.language {
  padding-bottom: 46px;

  .language-picker {
    float: none;
    margin-left: 20px;
  }
}

.change-metric {
  margin-right: 10px;
  margin-bottom: 8px;

  [class^="btn"] {
    cursor: pointer;
  }
}
</style>
